/**
 * Normalisation E.164 côté frontend v2.
 *
 * ⚠️ PORT de `apps/backend/src/common/utils/phone-validation.ts`
 * (`validateAndNormalizePhone`) — même table de préfixes, mêmes règles, mêmes
 * cas limites, à UNE divergence délibérée près (voir plus bas). Le monorepo
 * n'a pas de package partagé entre `apps/backend` et `apps/frontend` : en
 * créer un dépasserait le périmètre de ce lot. La duplication est donc
 * ASSUMÉE et signalée comme dette (candidat évident à un futur
 * `packages/shared`).
 *
 * DIVERGENCE ASSUMÉE : le backend termine par un « dernier recours » qui
 * préfixe `+` à toute suite de 7 à 15 chiffres non reconnue. Sur un composeur
 * d'agent, cela transformerait une frappe erronée en appel vers un numéro
 * arbitraire (potentiellement surtaxé) sans le moindre avertissement. Ici, un
 * numéro non reconnu est REFUSÉ avec un message : l'agent corrige, aucun appel
 * n'est passé. Le backend reste la dernière ligne de défense (regex du DTO).
 *
 * Pourquoi c'est indispensable (constat F-4 de la recette réelle du
 * 2026-07-22) : `CreateCallDto.toNumber` exige `^\+[1-9]\d{6,14}$`. Un agent
 * qui compose « 06 12 34 56 78 » (le geste naturel) recevait un 400 sec. La
 * normalisation doit donc avoir lieu AVANT le POST, pas dans un message
 * d'erreur.
 */

/** Mêmes préfixes que la table backend — ne pas diverger sans mettre à jour les deux. */
const COUNTRY_CODES: Record<string, string> = {
  '33': '+33', // France
  '972': '+972', // Israël
  '1': '+1', // USA/Canada
  '44': '+44', // Royaume-Uni
  '49': '+49', // Allemagne
  '39': '+39', // Italie
  '34': '+34', // Espagne
  '32': '+32', // Belgique
  '41': '+41', // Suisse
  '352': '+352', // Luxembourg
}

/** Pays par défaut des agents du CRM : un `06…` composé sans indicatif est français. */
export const DEFAULT_COUNTRY_CODE = '33'

export interface PhoneNormalizationResult {
  isValid: boolean
  /** Numéro au format E.164 (`+33612345678`), ou `null` si non normalisable. */
  normalized: string | null
  /** Message prêt à afficher (français) quand `isValid` est faux. */
  error?: string
}

/** Longueur E.164 valide : `+` suivi de 7 à 15 chiffres. */
function isValidE164Digits(digits: string): boolean {
  return /^\d{7,15}$/.test(digits)
}

/**
 * Valide et normalise un numéro vers E.164.
 *
 * Exemples (identiques à la spec backend) :
 *   '+33612345678'      → '+33612345678'
 *   '06 12 34 56 78'    → '+33612345678'  (indicatif par défaut)
 *   '0033612345678'     → '+33612345678'
 *   '33612345678'       → '+33612345678'  (indicatif détecté)
 *   'abc'               → invalide
 */
export function normalizePhoneToE164(
  phone: string,
  defaultCountryCode: string = DEFAULT_COUNTRY_CODE,
): PhoneNormalizationResult {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, normalized: null, error: 'Numéro requis' }
  }

  // Espaces, tirets, parenthèses, points : bruit de saisie.
  let cleaned = phone.replace(/[\s\-().]/g, '')
  if (!cleaned) {
    return { isValid: false, normalized: null, error: 'Numéro vide' }
  }

  // Déjà en E.164
  if (cleaned.startsWith('+')) {
    return isValidE164Digits(cleaned.slice(1))
      ? { isValid: true, normalized: cleaned }
      : {
          isValid: false,
          normalized: null,
          error: `Format international invalide : ${phone}`,
        }
  }

  // Préfixe international sans « + »
  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`
    return isValidE164Digits(cleaned.slice(1))
      ? { isValid: true, normalized: cleaned }
      : {
          isValid: false,
          normalized: null,
          error: `Format international invalide : ${phone}`,
        }
  }

  if (!/^\d+$/.test(cleaned)) {
    return {
      isValid: false,
      normalized: null,
      error: `Caractères invalides dans le numéro : ${phone}`,
    }
  }

  // Numéro local (0X…) → indicatif par défaut. Testé AVANT la détection
  // d'indicatif : sans ça, « 0612345678 » verrait son « 0 » ignoré et le
  // premier chiffre pris pour un indicatif.
  if (cleaned.startsWith('0') && defaultCountryCode) {
    const normalized = `+${defaultCountryCode}${cleaned.slice(1)}`
    if (isValidE164Digits(normalized.slice(1))) {
      return { isValid: true, normalized }
    }
  }

  // Indicatif présent mais sans « + » — préfixes les plus longs d'abord.
  for (const codeLength of [3, 2, 1]) {
    const prefix = cleaned.slice(0, codeLength)
    if (COUNTRY_CODES[prefix]) {
      const normalized = COUNTRY_CODES[prefix] + cleaned.slice(codeLength)
      if (isValidE164Digits(normalized.slice(1))) {
        return { isValid: true, normalized }
      }
    }
  }

  return {
    isValid: false,
    normalized: null,
    error: `Numéro non reconnu : ${phone} (attendu : +33612345678 ou 0612345678)`,
  }
}
